import { useEffect, useRef } from "react";

/**
 * Calls `fetcher()` once on mount, and again whenever the tab regains focus or
 * becomes visible. Used by Launchpad widgets so their data doesn't go stale
 * after the user edits a task on another page and comes back.
 *
 * Quiet on the eye: it does NOT toggle a loading spinner on the silent
 * refresh — callers control that. It just calls `fetcher` and lets the
 * widget update its own state.
 */
export default function useRefreshOnFocus(fetcher, deps = []) {
  // Keep a stable ref to the latest fetcher so the effect below doesn't
  // re-bind listeners on every render.
  const fetcherRef = useRef(fetcher);
  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  useEffect(() => {
    // Initial fetch
    fetcherRef.current?.();

    // Throttle to once per 2s so a flurry of focus/visibility events on tab
    // switching doesn't hammer the API.
    let lastRun = 0;
    const trigger = () => {
      const now = Date.now();
      if (now - lastRun < 2000) return;
      lastRun = now;
      fetcherRef.current?.();
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") trigger();
    };
    window.addEventListener("focus", trigger);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", trigger);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, deps);
}
